import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, BookOpen, FileText, Folder } from 'lucide-react';
import { useAllTopics } from '@/hooks/useAllTopics';
import SEO from '@/components/SEO';

const CategoryDocumentsPage = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const { topics } = useAllTopics();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const topicData = topics.find(topic => topic.id === categoryId);

  if (!topicData) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="pt-32 pb-20 bg-background">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="font-rockwell text-4xl font-semibold text-primary mb-6">
              Category Not Found
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              The requested category could not be found.
            </p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <SEO
        title={`${topicData.title} Documents | Freeman & Crown Law`}
        description={`Download legal documents and templates for ${topicData.title}. ${topicData.description}`}
        image="/lovable-uploads/8c630ffa-2bac-4996-be73-277885cc03f3.png"
        url={window.location.origin + `/documents/${categoryId}`}
        type="website"
      />
      <main className="pt-32 pb-20 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-6">
              {topicData.title} Documents
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Legal documents, templates, and resources related to {topicData.title.toLowerCase()}.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to={`/documents/${categoryId}/books`}>
              <Card className="group hover:shadow-lg transition-all duration-300 cursor-pointer border border-border rounded-lg h-full flex flex-col">
                <CardHeader className="text-center pb-4">
                  <div className="mx-auto mb-4 p-3 rounded-full bg-primary/10 w-fit group-hover:bg-primary/20 transition-colors hidden md:block">
                    <BookOpen className="h-8 w-8 text-primary" />
                  </div>
                  <CardTitle className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                    Books
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center flex-1 flex flex-col justify-between">
                  <p className="text-muted-foreground mb-4">
                    Legal books and comprehensive guides related to {topicData.title.toLowerCase()}.
                  </p>
                  <Button className="mt-4 w-full bg-gold-light hover:bg-gold-light/90 text-gold-foreground font-semibold border border-primary">
                    View Books
                    <BookOpen className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            </Link>

            <Link to={`/documents/${categoryId}/templates`}>
              <Card className="group hover:shadow-lg transition-all duration-300 cursor-pointer border border-border rounded-lg h-full flex flex-col">
                <CardHeader className="text-center pb-4">
                  <div className="mx-auto mb-4 p-3 rounded-full bg-primary/10 w-fit group-hover:bg-primary/20 transition-colors hidden md:block">
                    <FileText className="h-8 w-8 text-primary" />
                  </div>
                  <CardTitle className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                    Templates
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center flex-1 flex flex-col justify-between">
                  <p className="text-muted-foreground mb-4">
                    Legal document templates and forms for {topicData.title.toLowerCase()}.
                  </p>
                  <Button className="mt-4 w-full bg-gold-light hover:bg-gold-light/90 text-gold-foreground font-semibold border border-primary">
                    View Templates
                    <FileText className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            </Link>

            <Link to={`/documents/${categoryId}/other`}>
              <Card className="group hover:shadow-lg transition-all duration-300 cursor-pointer border border-border rounded-lg h-full flex flex-col">
                <CardHeader className="text-center pb-4">
                  <div className="mx-auto mb-4 p-3 rounded-full bg-primary/10 w-fit group-hover:bg-primary/20 transition-colors hidden md:block">
                    <Folder className="h-8 w-8 text-primary" />
                  </div>
                  <CardTitle className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                    Other Resources
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center flex-1 flex flex-col justify-between">
                  <p className="text-muted-foreground mb-4">
                    Additional resources and materials for {topicData.title.toLowerCase()}.
                  </p>
                  <Button className="mt-4 w-full bg-gold-light hover:bg-gold-light/90 text-gold-foreground font-semibold border border-primary">
                    View Resources
                    <Folder className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CategoryDocumentsPage;